import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { formatMoney, setLocaleContext } from '@/utils/currency';
import { resolveFileUrl } from '@/utils/fileUrl';
import { posApi } from '@/services/api';
import {
  requestCustomerDisplaySync,
  subscribeCustomerDisplay,
  type CustomerDisplayMessage,
} from '@/utils/customerDisplayChannel';

export interface CustomerDisplayState {
  companyName?: string;
  logoUrl?: string;
  currency?: string;
  country?: string;
  uiLanguage?: string;
  items: Array<{ name: string; qty: number; amount: number }>;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  status: 'idle' | 'active' | 'payment' | 'thankyou';
  thankYouMessage?: string;
}

const DEFAULT_STATE: CustomerDisplayState = {
  items: [],
  subtotal: 0,
  discount: 0,
  tax: 0,
  total: 0,
  status: 'idle',
};

function resolveDisplayCompanyCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('company')?.trim();
  if (fromUrl) {
    sessionStorage.setItem('saledesk-display-company', fromUrl);
    return fromUrl;
  }
  return sessionStorage.getItem('saledesk-display-company') || localStorage.getItem('company_code');
}

function applyLocale(data: Partial<CustomerDisplayState>) {
  if (!data.currency && !data.country) return;
  setLocaleContext({
    currency: data.currency || 'PKR',
    country: data.country || 'PK',
    uiLanguage: data.uiLanguage || 'en',
  });
}

function mergeCartPayload(prev: CustomerDisplayState, data: Record<string, unknown>): CustomerDisplayState {
  if (data.status === 'thankyou') {
    return {
      ...prev,
      status: 'thankyou',
      thankYouMessage: (data.thankYouMessage as string) || 'Thank you!',
      items: [],
      total: 0,
    };
  }
  applyLocale({
    currency: data.currency as string | undefined,
    country: data.country as string | undefined,
    uiLanguage: data.uiLanguage as string | undefined,
  });
  const logo = (data.logoUrl as string) || prev.logoUrl;
  return {
    ...prev,
    companyName: (data.companyName as string) || prev.companyName,
    logoUrl: logo ? resolveFileUrl(logo) : prev.logoUrl,
    currency: (data.currency as string) || prev.currency,
    country: (data.country as string) || prev.country,
    uiLanguage: (data.uiLanguage as string) || prev.uiLanguage,
    items: (data.items as CustomerDisplayState['items']) || [],
    subtotal: Number(data.subtotal) || 0,
    discount: Number(data.discount) || 0,
    tax: Number(data.tax) || 0,
    total: Number(data.total) || 0,
    status: (data.status as CustomerDisplayState['status']) || 'active',
  };
}

function handleMessage(
  payload: CustomerDisplayMessage,
  setState: Dispatch<SetStateAction<CustomerDisplayState>>,
) {
  if (payload.type === 'cart' && payload.data && typeof payload.data === 'object') {
    setState((prev) => mergeCartPayload(prev, payload.data as Record<string, unknown>));
    return;
  }
  if (payload.type === 'thankyou') {
    setState((prev) => ({
      ...prev,
      status: 'thankyou',
      thankYouMessage: (payload.message as string) || 'Thank you!',
      items: [],
      total: 0,
    }));
    setTimeout(
      () =>
        setState((prev) => ({
          ...DEFAULT_STATE,
          companyName: prev.companyName,
          logoUrl: prev.logoUrl,
          currency: prev.currency,
          country: prev.country,
          uiLanguage: prev.uiLanguage,
        })),
      8000,
    );
  }
}

export default function CustomerDisplayPage() {
  const [state, setState] = useState<CustomerDisplayState>(DEFAULT_STATE);

  useEffect(() => {
    const unsubscribe = subscribeCustomerDisplay((payload) => handleMessage(payload, setState));
    requestCustomerDisplaySync();

    const companyCode = resolveDisplayCompanyCode();
    const pullSnapshot = async () => {
      try {
        const res = await posApi.getCustomerDisplaySnapshot(companyCode || undefined);
        const data = res.data.message as Record<string, unknown> | undefined;
        if (data && typeof data === 'object') {
          setState((prev) => mergeCartPayload(prev, data));
        }
      } catch {
        /* offline or missing company code */
      }
    };

    void pullSnapshot();
    const poll = window.setInterval(pullSnapshot, 2000);

    return () => {
      unsubscribe();
      window.clearInterval(poll);
    };
  }, []);

  if (state.status === 'thankyou') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-600 to-primary-800 flex flex-col items-center justify-center text-white p-8">
        <div className="text-5xl font-bold mb-4">✓</div>
        <h1 className="text-4xl font-bold">{state.thankYouMessage || 'Thank you!'}</h1>
        <p className="text-xl mt-4 opacity-90">Please come again</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      <header className="p-6 border-b border-white/10 flex items-center gap-4">
        {state.logoUrl ? (
          <img src={state.logoUrl} alt="" className="h-14 w-auto object-contain" />
        ) : null}
        <h1 className="text-3xl font-bold">{state.companyName || 'Welcome'}</h1>
      </header>

      <main className="flex-1 p-6 overflow-auto">
        {state.items.length === 0 ? (
          <div className="h-full flex items-center justify-center text-2xl text-gray-500">
            Your items will appear here
          </div>
        ) : (
          <ul className="space-y-3">
            {state.items.map((line, i) => (
              <li key={i} className="flex justify-between items-center text-xl py-2 border-b border-white/10">
                <span>
                  {line.name} <span className="text-gray-400">× {line.qty}</span>
                </span>
                <span className="font-semibold">{formatMoney(line.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </main>

      <footer className="p-6 border-t border-white/10 space-y-2 text-xl">
        {state.items.length > 0 && state.subtotal > 0 && state.subtotal !== state.total && (
          <div className="flex justify-between text-gray-400">
            <span>Subtotal</span>
            <span>{formatMoney(state.subtotal)}</span>
          </div>
        )}
        {state.discount > 0 && (
          <div className="flex justify-between text-gray-400">
            <span>Discount</span>
            <span>-{formatMoney(state.discount)}</span>
          </div>
        )}
        {state.tax > 0 && (
          <div className="flex justify-between text-gray-400">
            <span>Tax</span>
            <span>{formatMoney(state.tax)}</span>
          </div>
        )}
        <div className="flex justify-between text-4xl font-bold pt-2">
          <span>Total</span>
          <span>{formatMoney(state.total)}</span>
        </div>
      </footer>
    </div>
  );
}
